// @ts-check
import {apiClient} from 'common/util/api-client';

import {appApiUrl} from '../utils';
import {BaseClient} from '../utils/base-client';

/**
 * @typedef {object} AnnotationUser
 * @property {number} id
 * @property {string} first_name
 * @property {string} last_name
 *
 * @typedef {object} Annotation
 * @property {number} id
 * @property {number} project_id
 * @property {string} date
 * @property {string} description
 * @property {object} [highchartsDate]
 * @property {AnnotationUser} [user]
 */

class AnnotationsClient extends BaseClient {
  /**
   * @param {Object} params
   * @param {number} params.projectId - the projectId you want to query
   * @param {number=} params.id - the id of the annotation you want to query (for annotation-specific queries)
   * @param {number} params.workspaceId - the workspace[dataview] id you want to query
   */
  buildEndpoint({projectId, id, workspaceId}) {
    let path = appApiUrl(`annotations/`, {projectId, workspaceId});
    if (id) {
      path = `${path}${id}/`;
    }
    return path;
  }

  /**
   * @param {object} params
   * @param {string} params.fromDate - start date in '%Y-%m-%d' format
   * @param {string} params.toDate - end date in '%Y-%m-%d' format
   */
  buildQueryParams({fromDate, toDate}) {
    return {
      fromDate,
      toDate,
    };
  }

  /**
   * @param {object} params
   * @param {number} params.projectId - the project id you want to query annotations for
   * @param {string} params.fromDate - start date in '%Y-%m-%d' format
   * @param {string} params.toDate - end date in '%Y-%m-%d' format
   * @param {number} params.workspaceId - the workspace[dataview] id you want to query annotations for
   * @returns {Promise<Annotation[]>} results
   */
  query({projectId, fromDate, toDate, workspaceId}) {
    // Intentionally turning off caching for annotations so we don't have to keep it in sync
    // when we create, update, delete
    return super.query({projectId, fromDate, toDate, workspaceId}, {clearCache: true});
  }

  /**
   * @param {object} params
   * @param {number} params.projectId - the project id this annotation belongs to
   * @param {object} params.annotation
   * @param {string} params.annotation.date - the date and time of the annotation in '%Y-%m-%d %H:%M:%S' (example: 2018-12-05 13:00:00)
   * @param {string} params.annotation.description - the content of the annotation
   * @param {number} params.workspaceId - the workspace[dataview] id this annotation belongs to
   * @returns {Promise<Annotation>} response
   */
  createAnnotation({projectId, annotation, workspaceId}) {
    return apiClient.post(this.buildEndpoint({projectId, workspaceId}), annotation);
  }

  /**
   * @param {object} params
   * @param {number} params.projectId - the project id this annotation belongs to
   * @param {number} params.id - the id of the annotation to update
   * @param {string} params.description - the updated description
   * @param {number} params.workspaceId - the workspace[dataview] id this annotation belongs to
   * @returns {Promise<Annotation>} response
   */
  updateAnnotation({projectId, id, description, workspaceId}) {
    return apiClient.patch(this.buildEndpoint({projectId, id, workspaceId}), {description});
  }

  /**
   * @param {object} params
   * @param {number} params.projectId - the project id this annotation belongs to
   * @param {number} params.id - the id of the annotation to delete
   * @param {number} params.workspaceId - the workspace[dataview] id this annotation belongs to
   * @returns {Promise<{id: number}>} response
   */
  deleteAnnotation({projectId, id, workspaceId}) {
    return apiClient.delete(this.buildEndpoint({projectId, id, workspaceId}));
  }

  processResponse(response) {
    return response.results;
  }
}

export const annotationsClient = new AnnotationsClient();
